import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

export default function AdminDashboardPage({
                                               kennelName = "Havaneser-Zucht",
                                               primaryColor = "#8b5e3c",
                                               secondaryColor = "#b9835a",
                                           }) {
    const navigate = useNavigate();

    const [userEmail, setUserEmail] = useState("");
    const [newsCount, setNewsCount] = useState(0);
    const [publishedCount, setPublishedCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [windowWidth, setWindowWidth] = useState(
        typeof window !== "undefined" ? window.innerWidth : 1200
    );

    const isMobile = windowWidth < 640;
    const isTablet = windowWidth >= 640 && windowWidth < 1024;

    const unpublishedCount = Math.max(newsCount - publishedCount, 0);

    const styles = useMemo(
        () =>
            createStyles({
                primaryColor,
                secondaryColor,
                isMobile,
                isTablet,
            }),
        [primaryColor, secondaryColor, isMobile, isTablet]
    );

    useEffect(() => {
        function handleResize() {
            setWindowWidth(window.innerWidth);
        }

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        async function loadDashboardData() {
            try {
                setIsLoading(true);
                setErrorMessage("");

                const { data: userData, error: userError } =
                    await supabase.auth.getUser();

                if (userError) {
                    throw userError;
                }

                const [allNewsResponse, publishedNewsResponse] =
                    await Promise.all([
                        supabase
                            .from("news")
                            .select("*", { count: "exact", head: true }),
                        supabase
                            .from("news")
                            .select("*", { count: "exact", head: true })
                            .eq("published", true),
                    ]);

                if (allNewsResponse.error) {
                    throw allNewsResponse.error;
                }

                if (publishedNewsResponse.error) {
                    throw publishedNewsResponse.error;
                }

                if (!isMounted) return;

                setUserEmail(userData?.user?.email || "");
                setNewsCount(allNewsResponse.count || 0);
                setPublishedCount(publishedNewsResponse.count || 0);
            } catch (error) {
                if (!isMounted) return;

                console.error("Dashboard konnte nicht geladen werden:", error);
                setErrorMessage(
                    "Die Dashboard-Daten konnten gerade nicht geladen werden."
                );
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        loadDashboardData();

        return () => {
            isMounted = false;
        };
    }, []);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate("/admin/login", { replace: true });
    };

    const stats = [
        {
            label: "Beiträge gesamt",
            value: newsCount,
            description: "Alle angelegten Neuigkeiten im System.",
            icon: "📰",
        },
        {
            label: "Veröffentlicht",
            value: publishedCount,
            description: "Aktuell sichtbare Beiträge auf der Webseite.",
            icon: "✅",
        },
        {
            label: "Entwürfe",
            value: unpublishedCount,
            description: "Beiträge, die noch nicht öffentlich sichtbar sind.",
            icon: "📝",
        },
    ];

    const actions = [
        {
            title: "News verwalten",
            description:
                "Beiträge erstellen, bearbeiten und Veröffentlichung steuern.",
            buttonLabel: "News öffnen",
            path: "/admin/news",
            icon: "📰",
        },
        {
            title: "Webseiten-Hinweise",
            description:
                "Kurze Hinweise verwalten, die prominent auf der Webseite angezeigt werden.",
            buttonLabel: "Hinweise öffnen",
            path: "/admin/notices",
            icon: "📌",
        },
        {
            title: "Zuchthunde / Elterntiere",
            description:
                "Hündinnen und Rüden inklusive Bilder und Beschreibungen verwalten.",
            buttonLabel: "Hunde öffnen",
            path: "/admin/dogs",
            icon: "🐶",
        },
        {
            title: "Würfe",
            description:
                "A-Wurf, B-Wurf und weitere Würfe übersichtlich pflegen.",
            buttonLabel: "Würfe öffnen",
            path: "/admin/litters",
            icon: "🐾",
        },
    ];

    const progressPercentage =
        newsCount > 0 ? Math.round((publishedCount / newsCount) * 100) : 0;

    return (
        <main style={styles.page}>
            <div style={styles.backgroundGlowOne} />
            <div style={styles.backgroundGlowTwo} />

            <section style={styles.container}>
                <motion.header
                    style={styles.hero}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45 }}
                >
                    <div style={styles.heroContent}>
                        <div style={styles.badge}>Adminbereich</div>

                        <h1 style={styles.title}>Willkommen zurück</h1>

                        <p style={styles.subtitle}>
                            Verwalte Inhalte, Hinweise, Hunde und Würfe für{" "}
                            <strong>{kennelName}</strong> zentral an einem Ort.
                        </p>

                        {userEmail && (
                            <div style={styles.userPill}>
                                <span style={styles.userDot} />
                                <span style={styles.userEmailText}>
                                    Angemeldet als {userEmail}
                                </span>
                            </div>
                        )}
                    </div>

                    <div style={styles.heroActions}>
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            style={styles.ghostButton}
                        >
                            Webseite ansehen
                        </button>

                        <button
                            type="button"
                            onClick={handleLogout}
                            style={styles.logoutButton}
                        >
                            Logout
                        </button>
                    </div>
                </motion.header>

                {errorMessage && (
                    <motion.div
                        style={styles.errorBox}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <strong>Hinweis:</strong> {errorMessage}
                    </motion.div>
                )}

                <section style={styles.statsGrid}>
                    {stats.map((stat, index) => (
                        <motion.article
                            key={stat.label}
                            style={styles.statCard}
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.38,
                                delay: 0.08 * index,
                            }}
                        >
                            <div style={styles.cardTopRow}>
                                <div style={styles.iconBubble}>{stat.icon}</div>
                                <span style={styles.statLabel}>{stat.label}</span>
                            </div>

                            <div style={styles.statValue}>
                                {isLoading ? "…" : stat.value}
                            </div>

                            <p style={styles.cardText}>{stat.description}</p>
                        </motion.article>
                    ))}
                </section>

                <section style={styles.contentGrid}>
                    <motion.article
                        style={styles.panel}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.15 }}
                    >
                        <div style={styles.sectionHeader}>
                            <div>
                                <p style={styles.eyebrow}>Schnellzugriff</p>
                                <h2 style={styles.sectionTitle}>
                                    Inhalte verwalten
                                </h2>
                            </div>
                        </div>

                        <div style={styles.actionGrid}>
                            {actions.map((action, index) => (
                                <motion.div
                                    key={action.path}
                                    style={styles.actionCard}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.35,
                                        delay: 0.2 + index * 0.07,
                                    }}
                                >
                                    <div style={styles.actionHeader}>
                                        <div style={styles.actionIcon}>
                                            {action.icon}
                                        </div>

                                        <div>
                                            <h3 style={styles.actionTitle}>
                                                {action.title}
                                            </h3>

                                            <p style={styles.actionText}>
                                                {action.description}
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => navigate(action.path)}
                                        style={styles.primaryButton}
                                    >
                                        {action.buttonLabel}
                                    </button>
                                </motion.div>
                            ))}
                        </div>
                    </motion.article>

                    <motion.aside
                        style={styles.sidePanel}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.25 }}
                    >
                        <p style={styles.eyebrow}>Status</p>

                        <h2 style={styles.sectionTitle}>Übersicht</h2>

                        <div style={styles.progressBox}>
                            <div style={styles.progressHeader}>
                                <span>Veröffentlichte News</span>
                                <strong>
                                    {isLoading
                                        ? "…"
                                        : `${publishedCount}/${newsCount}`}
                                </strong>
                            </div>

                            <div style={styles.progressTrack}>
                                <div
                                    style={{
                                        ...styles.progressFill,
                                        width: `${progressPercentage}%`,
                                    }}
                                />
                            </div>
                        </div>

                        <div style={styles.infoList}>
                            <div style={styles.infoItem}>
                                <span style={styles.infoDot} />
                                Inhalte regelmäßig prüfen und aktuell halten.
                            </div>

                            <div style={styles.infoItem}>
                                <span style={styles.infoDot} />
                                Neue Würfe und Hinweise zeitnah eintragen.
                            </div>

                            <div style={styles.infoItem}>
                                <span style={styles.infoDot} />
                                Bilder vor dem Upload möglichst komprimieren.
                            </div>
                        </div>
                    </motion.aside>
                </section>
            </section>
        </main>
    );
}

function createStyles({ primaryColor, secondaryColor, isMobile, isTablet }) {
    return {
        page: {
            position: "relative",
            minHeight: "100dvh",
            width: "100%",
            overflowX: "clip",
            background:
                "linear-gradient(135deg, #fbf7f1 0%, #f4eadf 48%, #fffaf4 100%)",
            padding: isMobile ? "0.75rem" : isTablet ? "1.25rem" : "2.5rem",
            color: "#2d241f",
            fontFamily:
                "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            boxSizing: "border-box",
        },

        backgroundGlowOne: {
            position: "absolute",
            top: isMobile ? "-7rem" : "-8rem",
            right: isMobile ? "-9rem" : "-7rem",
            width: isMobile ? "16rem" : "22rem",
            height: isMobile ? "16rem" : "22rem",
            borderRadius: "999px",
            background: `${secondaryColor}24`,
            filter: "blur(12px)",
            pointerEvents: "none",
        },

        backgroundGlowTwo: {
            position: "absolute",
            bottom: isMobile ? "-9rem" : "-10rem",
            left: isMobile ? "-9rem" : "-8rem",
            width: isMobile ? "17rem" : "24rem",
            height: isMobile ? "17rem" : "24rem",
            borderRadius: "999px",
            background: `${primaryColor}18`,
            filter: "blur(16px)",
            pointerEvents: "none",
        },

        container: {
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: "1180px",
            margin: "0 auto",
            boxSizing: "border-box",
        },

        hero: {
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            gap: isMobile ? "1rem" : "1.5rem",
            alignItems: isMobile ? "stretch" : "flex-start",
            padding: isMobile ? "1.1rem" : isTablet ? "1.4rem" : "2rem",
            borderRadius: isMobile ? "22px" : "28px",
            background: "rgba(255, 255, 255, 0.82)",
            border: "1px solid rgba(139, 94, 60, 0.14)",
            boxShadow: isMobile
                ? "0 16px 40px rgba(80, 49, 27, 0.1)"
                : "0 24px 70px rgba(80, 49, 27, 0.12)",
            backdropFilter: "blur(18px)",
            marginBottom: isMobile ? "0.9rem" : "1.25rem",
            boxSizing: "border-box",
        },

        heroContent: {
            maxWidth: "720px",
            minWidth: 0,
        },

        badge: {
            display: "inline-flex",
            alignItems: "center",
            width: "fit-content",
            padding: isMobile ? "0.38rem 0.65rem" : "0.45rem 0.75rem",
            borderRadius: "999px",
            background: `${primaryColor}14`,
            color: primaryColor,
            fontSize: isMobile ? "0.72rem" : "0.8rem",
            fontWeight: 800,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            marginBottom: isMobile ? "0.8rem" : "1rem",
        },

        title: {
            margin: 0,
            color: primaryColor,
            fontSize: isMobile ? "2rem" : isTablet ? "2.6rem" : "3.4rem",
            lineHeight: 1,
            letterSpacing: "-0.05em",
            fontWeight: 900,
        },

        subtitle: {
            margin: isMobile ? "0.8rem 0 0" : "1rem 0 0",
            maxWidth: "640px",
            color: "#6f625a",
            fontSize: isMobile ? "0.95rem" : "1rem",
            lineHeight: 1.65,
        },

        userPill: {
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            width: isMobile ? "100%" : "fit-content",
            maxWidth: "100%",
            marginTop: "1rem",
            padding: isMobile ? "0.65rem 0.75rem" : "0.65rem 0.85rem",
            borderRadius: isMobile ? "16px" : "999px",
            background: "#fff",
            color: "#55463c",
            border: "1px solid rgba(139, 94, 60, 0.12)",
            fontSize: isMobile ? "0.84rem" : "0.9rem",
            boxShadow: "0 10px 30px rgba(80, 49, 27, 0.08)",
            boxSizing: "border-box",
        },

        userEmailText: {
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },

        userDot: {
            flex: "0 0 auto",
            width: "0.55rem",
            height: "0.55rem",
            borderRadius: "999px",
            background: "#2eb872",
            boxShadow: "0 0 0 4px rgba(46, 184, 114, 0.12)",
        },

        heroActions: {
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, auto)",
            gap: "0.75rem",
            justifyContent: isMobile ? "stretch" : "flex-end",
            width: isMobile ? "100%" : "auto",
        },

        ghostButton: {
            width: isMobile ? "100%" : "auto",
            border: "1px solid rgba(139, 94, 60, 0.22)",
            background: "rgba(255, 255, 255, 0.7)",
            color: primaryColor,
            padding: "0.9rem 1rem",
            borderRadius: "999px",
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: "0 10px 25px rgba(80, 49, 27, 0.06)",
        },

        logoutButton: {
            width: isMobile ? "100%" : "auto",
            border: "none",
            background: primaryColor,
            color: "#fff",
            padding: "0.9rem 1.1rem",
            borderRadius: "999px",
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: `0 14px 28px ${primaryColor}38`,
        },

        errorBox: {
            padding: isMobile ? "0.9rem" : "1rem 1.1rem",
            borderRadius: "18px",
            background: "#fff4f0",
            border: "1px solid #ffd4c7",
            color: "#8a321f",
            marginBottom: isMobile ? "0.9rem" : "1.25rem",
            boxShadow: "0 12px 30px rgba(138, 50, 31, 0.08)",
            fontSize: isMobile ? "0.9rem" : "1rem",
        },

        statsGrid: {
            display: "grid",
            gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(3, minmax(0, 1fr))",
            gap: isMobile ? "0.75rem" : "1rem",
            marginBottom: isMobile ? "0.75rem" : "1rem",
        },

        statCard: {
            padding: isMobile ? "1rem" : "1.25rem",
            borderRadius: isMobile ? "20px" : "24px",
            background: "rgba(255, 255, 255, 0.88)",
            border: "1px solid rgba(139, 94, 60, 0.12)",
            boxShadow: isMobile
                ? "0 12px 32px rgba(80, 49, 27, 0.08)"
                : "0 18px 50px rgba(80, 49, 27, 0.09)",
            boxSizing: "border-box",
        },

        cardTopRow: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: isMobile ? "0.9rem" : "1.2rem",
        },

        iconBubble: {
            display: "grid",
            placeItems: "center",
            flex: "0 0 auto",
            width: isMobile ? "2.4rem" : "2.7rem",
            height: isMobile ? "2.4rem" : "2.7rem",
            borderRadius: "18px",
            background: `${secondaryColor}18`,
            fontSize: isMobile ? "1.2rem" : "1.35rem",
        },

        statLabel: {
            color: "#7a6b61",
            fontSize: isMobile ? "0.74rem" : "0.86rem",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            textAlign: "right",
        },

        statValue: {
            color: "#2d241f",
            fontSize: isMobile ? "2.2rem" : "2.55rem",
            lineHeight: 1,
            fontWeight: 900,
            letterSpacing: "-0.04em",
        },

        cardText: {
            margin: "0.75rem 0 0",
            color: "#75685f",
            lineHeight: 1.55,
            fontSize: isMobile ? "0.9rem" : "0.95rem",
        },

        contentGrid: {
            display: "grid",
            gridTemplateColumns:
                isMobile || isTablet
                    ? "1fr"
                    : "minmax(0, 1fr) minmax(280px, 360px)",
            gap: isMobile ? "0.75rem" : "1rem",
            alignItems: "start",
        },

        panel: {
            padding: isMobile ? "1rem" : "1.25rem",
            borderRadius: isMobile ? "22px" : "28px",
            background: "rgba(255, 255, 255, 0.84)",
            border: "1px solid rgba(139, 94, 60, 0.12)",
            boxShadow: isMobile
                ? "0 14px 38px rgba(80, 49, 27, 0.08)"
                : "0 20px 60px rgba(80, 49, 27, 0.1)",
            minWidth: 0,
            boxSizing: "border-box",
        },

        sidePanel: {
            padding: isMobile ? "1rem" : "1.25rem",
            borderRadius: isMobile ? "22px" : "28px",
            background: `linear-gradient(160deg, ${primaryColor}, ${secondaryColor})`,
            color: "#fff",
            boxShadow: `0 22px 60px ${primaryColor}35`,
            minWidth: 0,
            boxSizing: "border-box",
        },

        sectionHeader: {
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "1rem",
        },

        eyebrow: {
            margin: "0 0 0.35rem",
            color: "inherit",
            opacity: 0.72,
            fontSize: isMobile ? "0.72rem" : "0.78rem",
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
        },

        sectionTitle: {
            margin: 0,
            fontSize: isMobile ? "1.2rem" : "1.35rem",
            lineHeight: 1.2,
            letterSpacing: "-0.03em",
            fontWeight: 900,
        },

        actionGrid: {
            display: "grid",
            gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(2, minmax(0, 1fr))",
            gap: isMobile ? "0.75rem" : "1rem",
        },

        actionCard: {
            display: "flex",
            flexDirection: "column",
            gap: "0.9rem",
            minHeight: isMobile ? "auto" : "230px",
            padding: isMobile ? "0.95rem" : "1rem",
            borderRadius: isMobile ? "20px" : "22px",
            background: "#fff",
            border: "1px solid rgba(139, 94, 60, 0.1)",
            boxShadow: "0 14px 35px rgba(80, 49, 27, 0.07)",
            minWidth: 0,
            boxSizing: "border-box",
        },

        actionHeader: {
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            alignItems: isMobile ? "flex-start" : "stretch",
            gap: isMobile ? "0.85rem" : "0.9rem",
        },

        actionIcon: {
            display: "grid",
            placeItems: "center",
            flex: "0 0 auto",
            width: isMobile ? "2.65rem" : "3rem",
            height: isMobile ? "2.65rem" : "3rem",
            borderRadius: "18px",
            background: `${primaryColor}10`,
            fontSize: isMobile ? "1.25rem" : "1.5rem",
        },

        actionTitle: {
            margin: 0,
            color: "#2d241f",
            fontSize: isMobile ? "1rem" : "1.05rem",
            fontWeight: 900,
            letterSpacing: "-0.02em",
        },

        actionText: {
            margin: "0.45rem 0 0",
            color: "#74665d",
            lineHeight: 1.55,
            fontSize: isMobile ? "0.9rem" : "0.94rem",
        },

        primaryButton: {
            marginTop: isMobile ? "0.2rem" : "auto",
            width: "100%",
            border: "none",
            background: secondaryColor,
            color: "#fff",
            padding: isMobile ? "0.85rem 0.9rem" : "0.85rem 1rem",
            borderRadius: "16px",
            fontWeight: 900,
            cursor: "pointer",
            boxShadow: `0 12px 24px ${secondaryColor}30`,
        },

        progressBox: {
            marginTop: "1.25rem",
            padding: isMobile ? "0.9rem" : "1rem",
            borderRadius: "20px",
            background: "rgba(255, 255, 255, 0.14)",
            border: "1px solid rgba(255, 255, 255, 0.18)",
        },

        progressHeader: {
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "0.75rem",
            fontSize: isMobile ? "0.9rem" : "0.94rem",
        },

        progressTrack: {
            width: "100%",
            height: "0.7rem",
            overflow: "hidden",
            borderRadius: "999px",
            background: "rgba(255, 255, 255, 0.22)",
        },

        progressFill: {
            height: "100%",
            borderRadius: "999px",
            background: "#fff",
            transition: "width 0.45s ease",
        },

        infoList: {
            display: "grid",
            gap: "0.8rem",
            marginTop: "1.25rem",
        },

        infoItem: {
            display: "flex",
            alignItems: "flex-start",
            gap: "0.65rem",
            color: "rgba(255, 255, 255, 0.88)",
            lineHeight: 1.55,
            fontSize: isMobile ? "0.9rem" : "0.94rem",
        },

        infoDot: {
            flex: "0 0 auto",
            width: "0.55rem",
            height: "0.55rem",
            borderRadius: "999px",
            background: "#fff",
            marginTop: "0.45rem",
        },
    };
}